import './affinity-card.scss';

import type { PropsWithChildren, ReactNode } from 'react';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { ItemCard } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';

const affinityCardClassName =
  'AffinityCard grid p-comfy-lg md:pl-comfy-xl md:pr-comfy-lg @container w-full';

export const AffinityCard: React.FC<PropsWithChildren> = ({ children }) => {
  return <ItemCard className={affinityCardClassName}>{children}</ItemCard>;
};

/**
 * Marks its child as the interactive surface of an AffinityCard so the card's
 * hover styles fire from a real <a> or <button>. Renders no DOM of its own —
 * it slots its className onto the child, so pass exactly one element and any
 * className already on that child will be preserved.
 */
export const AffinityCardInteractive: React.FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className }) => {
  return (
    <ItemCard
      asChild
      className={cn(
        affinityCardClassName,
        'AffinityCard--interactive',
        className,
      )}
    >
      {children}
    </ItemCard>
  );
};

export const AffinityCardGraphic: React.FC<
  PropsWithChildren<{
    className?: string;
    align?: 'top' | 'center';
  }>
> = ({ className, align = 'top', children }) => {
  return (
    <div
      className={cn('AffinityCard__graphic', className, {
        'AffinityCard__graphic--center': align === 'center',
      })}
    >
      {children}
    </div>
  );
};

export const AffinityCardSummary: React.FC<
  PropsWithChildren<{
    title: string;
    badge?: ReactNode;
    summary?: string;
  }>
> = ({ title, badge, summary, children }) => {
  return (
    <div className="AffinityCard__summary flex flex-col">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-snug-xl">
        <h3 className="label-potent label-xl label-neutral-amplified">
          {title}
        </h3>
        <div className="AffinityCard__badge">{badge}</div>
      </div>

      {summary && (
        <p className="label-moderate label-lg py-snug-xl label-neutral-mild">
          {summary}
        </p>
      )}
      {children}
    </div>
  );
};

export const AffinityCardStat: React.FC<{
  title: string;
  stat: string;
}> = ({ title, stat }) => {
  return (
    <div className="flex flex-1 min-w-0 flex-col gap-snug-xl">
      <span className="label-moderate label-sm md:label-md label-neutral-subtle">
        {title}
      </span>
      <span className="label-moderate label-neutral-potent label-md md:label-lg">
        {stat}
      </span>
    </div>
  );
};

export const AffinityCardStats: React.FC<PropsWithChildren> = ({
  children,
}) => {
  return (
    <div className="AffinityCard__stats flex items-start gap-snug-xl sm:gap-roomy-xs pt-comfy-md md:pt-comfy-sm">
      {children}
    </div>
  );
};

export const AffinityCardSkeleton: React.FC = () => {
  return (
    <AffinityCard>
      <AffinityCardGraphic>
        <Skeleton
          width={100}
          height={100}
          borderRadius={'var(--outline-radius-lg)'}
        />
      </AffinityCardGraphic>
      <AffinityCardSummary title={''}>
        <Skeleton width="70%" height={28} className="rounded-md" />
        <Skeleton width="100%" height={16} className="mt-snug-xl rounded-md" />
        <div className="md:hidden pt-snug-xl">
          <Skeleton width="80%" height={16} className="rounded-md" />
        </div>
      </AffinityCardSummary>
      <AffinityCardStats>
        {['first', 'second', 'third'].map((displayType) => (
          <div key={displayType} className="flex flex-1 flex-col gap-snug-xl">
            <Skeleton width="80%" height={16} className="rounded-md" />
            <Skeleton width="60%" height={16} className="rounded-md" />
          </div>
        ))}
      </AffinityCardStats>
    </AffinityCard>
  );
};
